import { useAppConfig } from "~/app/config/useAppConfig";
import { type FC, useCallback, useMemo, useState } from "react";
import { Button, Stack } from "~/common/mui";
import OrderTable from "~/features/orders/orderDetails/components/OrderDetails/OrderTable";
import OrderTotals from "~/features/cart/cart/components/OrderTotals";
import OrderDetailsAddToCartFlow from "~/features/orders/orderDetails/components/AddToCart/OrderDetailsAddToCartFlow";
import OrderDetailsAddToWishlistFlow from "~/features/orders/orderDetails/components/AddToWishlist/OrderDetailsAddToWishlistFlow";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import {
  getOrderLineItemSurchargeValue,
  getOrderLineItemTotalPrice,
} from "~/features/orders/common/utils/OrderLineItemUtils";
import { sum } from "~/utils/PriceUtils";
import { type DetailedOrderData, type DetailedOrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { type CaseDataObject } from "~/features/orders/orderDetails/components/ServiceCases/types";
import { strictIsNonNull } from "~/common/utils/reducers";
import { ATCBar } from "~/features/orders/common/components/ATCBar";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

interface OrderDetailsProps {
  data: DetailedOrderData;
  serviceCases?: Record<string, CaseDataObject[]>;
}

interface selectedATCItemsProps {
  selectedLineItems: number;
  selectedQuantity: number;
  totalPrice: PriceData;
}

type Accumulator = {
  totalPieces: number;
  totalPrice: number;
  currencyIso: string;
};

const selectedATCItemsDefaultValue = {
  selectedLineItems: 0,
  selectedQuantity: 0,
  totalPrice: { value: 0, currencyIso: "" },
};

const OrderDetails: FC<OrderDetailsProps> = ({ data, serviceCases }) => {
  const { t } = useTranslation();

  const canAddToCart = userHasPermission(Permission.ADD_TO_CART);
  const canAddToWishlist = userHasPermission(Permission.ADD_TO_WISHLIST);
  const showCheckboxes = canAddToCart || canAddToWishlist;
  const [selected, setSelected] = useState<number[]>([]);
  const selectedItems = selected.map((index) => data.products[index]);

  const { val: showAddToCart, setTrue: openAddToCart, setFalse: closeAddToCart } = useBooleanState(false);
  const { val: showAddtoWishlist, setTrue: openAddToWishlist, setFalse: closeAddToWishlist } = useBooleanState(false);

  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;

  const selectedATCItems = useMemo(() => getSelectedATCItems(selectedItems), [selectedItems]);

  const selectItem = useCallback((itemIndex: number) => {
    setSelected((selected) => {
      if (selected.indexOf(itemIndex) === -1) {
        return selected.concat(itemIndex);
      } else {
        return selected.filter((item) => item !== itemIndex);
      }
    });
  }, []);

  const selectAll = useCallback(
    (newValue: boolean) => {
      if (newValue) {
        setSelected(
          data ? data.products.map((_, index) => (_.product.url ? index : null)).filter(strictIsNonNull) : [],
        );
      } else {
        setSelected([]);
      }
    },
    [data],
  );

  const totalSurcharge = useMemo(() => {
    return sum(...data.products.map((lineItem) => getOrderLineItemSurchargeValue(lineItem)));
  }, [data]);

  const totalProducts = useMemo(() => {
    let sum = 0;
    data.products.map((product) => {
      if (product.subEntries && product.subEntries?.length > 0) {
        sum += product.subEntries?.length ?? 0;
      } else {
        sum += 1;
      }
    });
    return sum;
  }, [data]);

  const constainsSurcharge = totalSurcharge.value > 0;

  return (
    <>
      <div className="d-flex flex-justify-between item-delivery-title-holder mt-30">
        <p className="m-0 headline-small">{t("orderDetailsPage.header.toBeOrdered", { count: totalProducts })}</p>

        <Stack className={"scha__screen-only"} gap={2}>
          {canAddToWishlist && (
            <Button
              disabled={selected.length === 0}
              color={canAddToCart ? "secondary" : "primary"}
              startIcon={<BookmarkIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
              onClick={openAddToWishlist}
              data-test-id={"add-checked-to-wishlist"}
            >
              {t("page.orderDetails.addToWishlist.button")}
            </Button>
          )}

          {canAddToCart && (
            <Button
              disabled={webservicesDisabled || selected.length === 0}
              startIcon={<ShoppingCartIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
              onClick={openAddToCart}
              data-test-id={"add-checked-to-cart"}
            >
              {t("orderDetailsPage.reorderButton")}
            </Button>
          )}
        </Stack>
      </div>

      <OrderTable
        className={"mb-4"}
        selected={selected}
        selectItem={selectItem}
        selectAll={selectAll}
        selectable={showCheckboxes}
        orderDetailsData={data}
        serviceCaseData={serviceCases}
      />

      <div className="scha__screen-only scha__order-details-display__totals--wrapper">
        <div className="scha__order-details-display__totals">
          <OrderTotals
            showPriceOnRequest={false}
            weight={data.totalWeight}
            totalValue={data.totalPrice}
            surcharge={totalSurcharge.value > 0 ? totalSurcharge : undefined}
          />
        </div>
      </div>
      {<IncludesSurchargeDisclaimer hasSurcharge={constainsSurcharge} className={"mt-30"} />}

      {showAddToCart && <OrderDetailsAddToCartFlow onClose={closeAddToCart} selectedOrderItems={selectedItems} />}
      {showAddtoWishlist && (
        <OrderDetailsAddToWishlistFlow onClose={closeAddToWishlist} selectedOrderItems={selectedItems} />
      )}

      {selectedATCItems.selectedLineItems > 0 && (
        <ATCBar
          selectedLineItems={selectedATCItems.selectedLineItems}
          selectedQuantity={selectedATCItems.selectedQuantity}
          totalPrice={selectedATCItems.totalPrice}
          onAddToCart={openAddToCart}
        />
      )}
    </>
  );
};

export default OrderDetails;

const getSelectedATCItems = (selectedItems: DetailedOrderLineItemData[]): selectedATCItemsProps => {
  if (selectedItems.length === 0) {
    return selectedATCItemsDefaultValue;
  }

  const currencyIso = getOrderLineItemTotalPrice(selectedItems[0]).currencyIso;

  const { totalPieces, totalPrice } = selectedItems.reduce<Accumulator>(
    (acc, item) => {
      acc.totalPieces += item.confirmedQty;

      const priceData = getOrderLineItemTotalPrice(item);
      if (priceData.currencyIso !== currencyIso) {
        throw new Error("Mismatched currencies in selected items");
      }
      acc.totalPrice += priceData.value;

      return acc;
    },
    { totalPieces: 0, totalPrice: 0, currencyIso: "" },
  );

  return {
    selectedLineItems: selectedItems.length,
    selectedQuantity: totalPieces,
    totalPrice: {
      currencyIso,
      value: totalPrice,
    },
  };
};
